import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, clearToken } from "@/utils/adminApi";
import { Plus, LogOut, Pencil, Search, ChevronRight, Settings, ArrowUp, ArrowDown } from "lucide-react";
import { useTourGuide, HelpButton } from "@/components/TourGuide";
import { AccessCodeSettings } from "@/components/admin/AccessCodeSettings";

function getPriceLabel(tour) {
  if (tour.price_type === "call") return "Call for prices";
  if (tour.price_type === "tiered" && tour.price_tiers?.length > 0) {
    return `From $${tour.price_tiers[tour.price_tiers.length - 1]?.price || tour.price}/pp`;
  }
  return `$${tour.price}/pp`;
}

function TourRow({ tour, position, total, onClick, onMove, moving }) {
  const isFirst = position === 0;
  const isLast = position === total - 1;
  const stop = (e) => e.stopPropagation();

  return (
    <div onClick={onClick}
      className="flex items-center gap-4 p-4 border border-white/5 hover:border-[#C5A059]/30 cursor-pointer transition-colors group"
      data-testid={`admin-tour-${tour.id}`}>
      <div className="flex flex-col gap-1 shrink-0" onClick={stop}>
        <button
          type="button"
          disabled={isFirst || moving}
          onClick={() => onMove(tour.id, "up")}
          aria-label="Move up"
          data-testid={`move-up-${tour.id}`}
          className="w-7 h-7 flex items-center justify-center border border-white/10 text-white/60 hover:text-[#C5A059] hover:border-[#C5A059]/40 disabled:opacity-20 disabled:hover:text-white/60 disabled:hover:border-white/10 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowUp size={13} />
        </button>
        <button
          type="button"
          disabled={isLast || moving}
          onClick={() => onMove(tour.id, "down")}
          aria-label="Move down"
          data-testid={`move-down-${tour.id}`}
          className="w-7 h-7 flex items-center justify-center border border-white/10 text-white/60 hover:text-[#C5A059] hover:border-[#C5A059]/40 disabled:opacity-20 disabled:hover:text-white/60 disabled:hover:border-white/10 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowDown size={13} />
        </button>
      </div>
      <img src={tour.image_url} alt="" className="w-16 h-16 object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <h3 className="font-sans text-white text-sm font-medium truncate">{tour.title}</h3>
        <p className="font-sans text-white/40 text-xs truncate">{tour.category} &middot; {tour.duration} &middot; {tour.gallery?.length || 0} photos</p>
        <p className="font-sans text-[#C5A059] text-xs mt-1">{getPriceLabel(tour)}</p>
      </div>
      <div className="flex items-center gap-2">
        <Pencil size={14} className="text-white/20 group-hover:text-[#C5A059] transition-colors" />
        <ChevronRight size={14} className="text-white/20" />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tours, setTours] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  const [moving, setMoving] = useState(false);
  const nav = useNavigate();
  const { GuideComponent, restart } = useTourGuide("dashboard");

  useEffect(() => {
    adminApi.get("/api/admin/tours")
      .then(r => setTours(r.data))
      .catch(() => nav("/admin"));
  }, []);

  const logout = async () => {
    await adminApi.post("/api/auth/logout").catch(() => {});
    clearToken();
    nav("/admin");
    window.location.reload();
  };

  const move = async (tourId, direction) => {
    if (moving) return;
    // Optimistic swap in local state for instant feedback
    setMoving(true);
    setTours((prev) => {
      const idx = prev.findIndex(t => t.id === tourId);
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || swapIdx < 0 || swapIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
    try {
      await adminApi.post(`/api/admin/tours/${tourId}/move`, { direction });
      // Re-sync with server to keep sort_order fresh
      const r = await adminApi.get("/api/admin/tours");
      setTours(r.data);
    } catch {
      // On failure, refetch to restore true order
      const r = await adminApi.get("/api/admin/tours").catch(() => ({ data: null }));
      if (r.data) setTours(r.data);
    }
    setMoving(false);
  };

  const categories = [...new Set(tours.map(t => t.category))];
  const filtered = tours.filter(t => {
    const matchSearch = t.title.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === "all" || t.category === catFilter;
    return matchSearch && matchCat;
  });
  const isFiltering = search.trim() !== "" || catFilter !== "all";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {GuideComponent}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-xl">Obsidian Admin</h1>
          <p className="font-sans text-white/40 text-xs">Tour Management</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => nav("/admin/tours/new")} data-testid="new-tour-btn"
            className="flex items-center gap-2 bg-[#C5A059] text-black font-sans text-xs tracking-wider uppercase px-4 py-2 hover:bg-[#A8853D] transition-colors">
            <Plus size={14} /> New Tour
          </button>
          <HelpButton onClick={restart} />
          <button
            onClick={() => setShowSettings(s => !s)}
            data-testid="admin-settings-btn"
            className={`transition-colors ${showSettings ? "text-[#C5A059]" : "text-white/40 hover:text-white"}`}
            aria-label="Security settings"
          >
            <Settings size={18} />
          </button>
          <button onClick={logout} data-testid="admin-logout-btn" className="text-white/40 hover:text-white transition-colors">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {showSettings && (
        <div className="px-6 py-4 border-b border-white/5">
          <AccessCodeSettings />
        </div>
      )}

      <div className="px-6 py-4 flex flex-col md:flex-row gap-4 border-b border-white/5">
        <div className="flex items-center gap-2 border border-white/10 px-3 py-2 flex-1">
          <Search size={14} className="text-white/30" />
          <input type="text" placeholder="Search tours..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent text-white font-sans text-sm outline-none flex-1 placeholder:text-white/20"
            data-testid="admin-search-input" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="bg-[#0A0A0A] border border-white/10 text-white font-sans text-sm px-3 py-2 outline-none"
          data-testid="admin-cat-filter">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <p className="font-sans text-white/40 text-xs">{filtered.length} tours</p>
          {isFiltering && (
            <p className="font-sans text-[#C5A059]/70 text-[11px]">
              Clear filters to reorder
            </p>
          )}
        </div>
        <div className="space-y-2">
          {filtered.map((tour) => {
            // Position is based on the FULL list, not the filtered view, so
            // arrows always reflect the true global order.
            const globalIdx = tours.findIndex(t => t.id === tour.id);
            return (
              <TourRow
                key={tour.id}
                tour={tour}
                position={globalIdx}
                total={tours.length}
                onClick={() => nav(`/admin/tours/${tour.id}`)}
                onMove={isFiltering ? () => {} : move}
                moving={moving || isFiltering}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
